import React, { useCallback, useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { changerLangueTemporaire, LANGUES } from '../../i18n';
import { buildApiUrl } from '../../utils/api';
import { BadgeStatut, BadgeUrgent, BoutonRequete, styleChamp } from './FenetreRequete';
import { ChoixPhotos } from './ChampsRequete';
import { formaterDateHeure } from './RequeteDetailModal';

/**
 * Page PUBLIQUE d'une requête (lien personnel reçu par courriel, sans compte).
 * Un contact externe (ex. travaux publics) lit la requête et la commente tant qu'elle
 * est ouverte. Français par défaut (langue du système) ; bouton FR / EN en haut
 * (le lien « English » du courriel ouvre `?lang=en`).
 */
const RequetePublique = () => {
  const { t, i18n } = useTranslation();
  const { tenantSlug, jeton } = useParams();
  const [searchParams] = useSearchParams();
  const [requete, setRequete] = useState(null);
  const [introuvable, setIntrouvable] = useState(false);
  const [texte, setTexte] = useState('');
  const [photos, setPhotos] = useState([]);
  const [envoi, setEnvoi] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => {
    const lang = (searchParams.get('lang') || '').toLowerCase();
    changerLangueTemporaire(LANGUES.includes(lang) ? lang : 'fr');
  }, [searchParams]);

  const url = buildApiUrl(tenantSlug, `/public/requetes/${encodeURIComponent(jeton || '')}`);
  const charger = useCallback(async () => {
    try {
      const r = await fetch(url);
      if (!r.ok) throw new Error(String(r.status));
      setRequete(await r.json());
    } catch (e) {
      setIntrouvable(true);
    }
  }, [url]);
  useEffect(() => { charger(); }, [charger]);

  const publier = async () => {
    setEnvoi(true);
    setMessage('');
    try {
      const r = await fetch(`${url}/commentaires`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ texte: texte.trim(), photos_base64: photos }),
      });
      if (!r.ok) throw new Error(String(r.status));
      setRequete(await r.json());
      setTexte('');
      setPhotos([]);
      setMessage(t('requetes.publique.merci'));
    } catch (e) {
      setMessage(t('requetes.publique.erreurEnvoi'));
    } finally {
      setEnvoi(false);
    }
  };

  const bascule = (
    <div role="group" aria-label={t('requetes.publique.langue')} style={{ display: 'flex', justifyContent: 'flex-end', gap: 4, marginBottom: '0.75rem' }}>
      {LANGUES.map((l) => {
        const actif = i18n.language === l;
        return (
          <button type="button" key={l} lang={l} aria-pressed={actif} onClick={() => changerLangueTemporaire(l)}
            style={{ padding: '0.3rem 0.7rem', fontSize: '0.8rem', fontWeight: 700, borderRadius: 6, cursor: 'pointer',
              border: `1px solid ${actif ? '#dc2626' : '#d1d5db'}`, background: actif ? '#dc2626' : 'white', color: actif ? 'white' : '#1f2937' }}>
            {l === 'en' ? 'ENG' : 'FR'}
          </button>
        );
      })}
    </div>
  );

  const page = (contenu) => (
    <div style={{ minHeight: '100vh', background: '#f3f4f6', padding: '1rem', fontFamily: 'system-ui, -apple-system, sans-serif', color: '#1f2937' }}>
      <div style={{ maxWidth: 680, margin: '0 auto' }}>{bascule}{contenu}</div>
    </div>
  );

  if (introuvable) {
    return page(
      <div style={{ background: 'white', borderRadius: 12, padding: '1.5rem', textAlign: 'center', border: '1px solid #e5e7eb' }}>
        <h2 style={{ margin: 0 }}>{t('requetes.publique.lienInvalide')}</h2>
        <p style={{ color: '#64748b' }}>{t('requetes.publique.lienInvalideAide')}</p>
      </div>
    );
  }
  if (!requete) return page(<p style={{ textAlign: 'center', color: '#64748b' }}>{t('requetes.chargement')}</p>);

  const ouvert = requete.statut === 'ouvert';
  return page(
    <div style={{ background: 'white', borderRadius: 12, border: '1px solid #e5e7eb', overflow: 'hidden' }}>
      <div style={{ background: '#fef2f2', padding: '1rem 1.25rem', borderBottom: '1px solid #fecaca' }}>
        <p style={{ margin: 0, fontSize: '0.8rem', color: '#64748b' }}>{requete.service}</p>
        <h1 style={{ margin: '4px 0', fontSize: '1.15rem', color: '#991b1b', display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          <span>{t('requetes.requeteNumero', { numero: requete.numero })} — {requete.vehicule_nom} | {requete.categorie_nom}</span>
          {requete.priorite === 'urgente' && <BadgeUrgent texte={t('requetes.urgentMaj')} />}
          <BadgeStatut ouvert={ouvert} texte={t(`requetes.statut.${requete.statut}`)} />
        </h1>
      </div>
      <div style={{ padding: '1rem 1.25rem' }}>
        {(requete.fil || []).map((e, i) => (
          <div key={i} style={{ padding: '10px 0', borderBottom: '1px solid #f1f5f9' }}>
            <div style={{ fontSize: '0.82rem' }}>
              <span style={{ color: '#dc2626', fontWeight: 600 }}>{formaterDateHeure(e.date)}</span>{' '}
              <strong>{e.auteur_nom}</strong>{' '}
              <span style={{ color: '#64748b' }}>{t(`requetes.fil.${e.type}`)}</span>
            </div>
            {e.texte && <div style={{ whiteSpace: 'pre-wrap', marginTop: 4 }}>{e.texte}</div>}
            {(e.photos_urls || []).length > 0 && (
              <div style={{ display: 'flex', gap: 6, marginTop: 6, flexWrap: 'wrap' }}>
                {e.photos_urls.map((u) => (
                  <a key={u} href={u} target="_blank" rel="noreferrer">
                    <img src={u} alt="" style={{ width: 80, height: 80, objectFit: 'cover', borderRadius: 6, border: '1px solid #e5e7eb' }} />
                  </a>
                ))}
              </div>
            )}
          </div>
        ))}
        {requete.peut_commenter && (
          <div style={{ marginTop: 14 }}>
            <label htmlFor="rq-pub-commentaire" style={{ display: 'block', fontWeight: 600, marginBottom: 4 }}>{t('requetes.ajouterCommentaire')}</label>
            <textarea id="rq-pub-commentaire" value={texte} onChange={(e) => setTexte(e.target.value)} style={{ ...styleChamp, minHeight: 90, marginBottom: 8 }} />
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
              <ChoixPhotos photos={photos} onChange={setPhotos} />
              <BoutonRequete onClick={publier} disabled={envoi || (!texte.trim() && !photos.length)}>
                {envoi ? t('requetes.envoiEnCours') : t('requetes.publier')}
              </BoutonRequete>
            </div>
          </div>
        )}
        {!ouvert && <p style={{ marginTop: 12, color: '#475569' }}>{t('requetes.publique.fermee')}</p>}
        {ouvert && !requete.externe && <p style={{ marginTop: 12, color: '#475569' }}>{t('requetes.publique.commenterDansPfm')}</p>}
        {message && <p role="status" style={{ marginTop: 10, color: '#166534' }}>{message}</p>}
      </div>
    </div>
  );
};

export default RequetePublique;
